import { useState, type FormEvent } from 'react';
import SectionHeading from './SectionHeading';

const FORM_URL = 'https://readdy.ai/api/form/daf8k9jep053ijub1l50';

const types = ['综艺/影视', '明星动态', '演出/票房', '时尚/红毯', '品牌/商业', '社会热议'];

type Status = 'idle' | 'sending' | 'success' | 'error';

const inputCls =
  'w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200';

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [msg, setMsg] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐字段：非空则静默按成功处理，不真正提交
    const honey = String(data.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      setMsg('已收到你的投稿，我们会尽快与您联系。');
      form.reset();
      return;
    }
    data.delete('website_alt');

    setStatus('sending');
    setMsg('');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const ok = res.ok && parsed?.code === 'OK';
      if (ok) {
        setStatus('success');
        setMsg('投稿成功！感谢认真吃瓜的你，审核通过后就会安排上墙。');
        form.reset();
      } else {
        setStatus('error');
        setMsg(
          serverMsg && typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : '投稿提交失败，请稍后再试。',
        );
      }
    } catch {
      setStatus('error');
      setMsg('网络好像开小差了，请稍后重试。');
    }
  }

  return (
    <section id="submit" className="scroll-mt-24 bg-background-100">
      <div className="mx-auto w-full max-w-4xl px-4 md:px-8 py-16 md:py-20">
        <SectionHeading
          eyebrow="投稿 / 私信"
          title="把瓜交给我们"
          accentWord="或悄悄说"
          desc="来料投稿可公开上头条；私信默认为你保密，仅在你授权并脱敏后才可能用于报道。"
        />

        <form
          data-readdy-form="吃瓜日报粉丝投稿表单"
          onSubmit={handleSubmit}
          className="mt-8 rounded-2xl border border-background-200 bg-background-50 p-5 md:p-8"
        >
          {/* 蜜罐反垃圾字段 */}
          <div className="field-reserved-check" aria-hidden="true">
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="nick" className="mb-1.5 block text-sm font-medium text-foreground-900">
                你的称呼 / 署名 <span className="text-primary-500">*</span>
              </label>
              <input
                id="nick"
                name="nickname"
                type="text"
                required
                maxLength={30}
                placeholder="如：前排瓜子@小鹿（可匿名）"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact" className="mb-1.5 block text-sm font-medium text-foreground-900">
                联系邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                id="contact"
                name="email"
                type="email"
                required
                maxLength={80}
                placeholder="用于核对与联系，仅编辑可见"
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="topicType" className="mb-1.5 block text-sm font-medium text-foreground-900">
              来料类型 <span className="text-primary-500">*</span>
            </label>
            <select id="topicType" name="topicType" required defaultValue="" className={inputCls}>
              <option value="" disabled>
                请选择这口瓜属于哪个圈子
              </option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between">
              <label htmlFor="content" className="block text-sm font-medium text-foreground-900">
                爆料内容 <span className="text-primary-500">*</span>
              </label>
              <span className="text-xs text-foreground-700">请尽量描述清楚时间、地点与经过</span>
            </div>
            <textarea
              id="content"
              name="content"
              required
              maxLength={500}
              rows={5}
              placeholder="把你知道的、看到的情况尽量说清楚。涉及他人的请脱敏处理，我们也会再次打码。"
              className={`${inputCls} resize-y`}
            ></textarea>
            <p className="mt-1 text-right text-xs text-foreground-700">500 字以内</p>
          </div>

          <label className="mt-4 flex items-start gap-2.5 text-xs text-foreground-700">
            <input
              type="checkbox"
              name="agreed"
              required
              className="mt-0.5 h-4 w-4 shrink-0 accent-primary-500"
            />
            <span>
              我确认所投内容真实客观，愿为来料负责；同意编辑对稿件进行必要的核实与脱敏处理，不涉及他人隐私与侵权内容。
            </span>
          </label>

          <button
            type="submit"
            disabled={status === 'sending'}
            className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === 'sending' ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill" aria-hidden="true"></i>提交给编辑部
              </>
            )}
          </button>

          {status === 'success' && (
            <div
              className="mt-4 flex items-center gap-2 rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900"
              role="status"
            >
              <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>
              {msg}
            </div>
          )}
          {status === 'error' && (
            <div
              className="mt-4 flex items-center gap-2 rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-700"
              role="alert"
            >
              <i className="ri-error-warning-fill" aria-hidden="true"></i>
              {msg}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}